/* eslint-disable @khanacademy/ts-no-error-suppressions */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";

import type {Story} from "./queries/stories-query.tsx";
import StoryFocus from "./story-focus.tsx";
import StoryForm from "./story-form.tsx";
import StoryTeaser from "./story-teaser.tsx";

type ModalPagerProps = {
    activeIndex: number | null | undefined;
    onClose: () => void;
    onSwitch: (arg1: number) => void;
    pages: Array<React.ReactElement<React.ComponentProps<typeof StoryFocus>>>;
};

class ModalPager extends React.Component<ModalPagerProps> {
    componentDidMount() {
        document.body?.classList.toggle(
            "modal-pager-active",
            this.props.activeIndex != null,
        );
        window.addEventListener("keydown", this.handleWindowKeyDown, false);
    }

    componentWillUnmount() {
        document.body?.classList.remove("modal-pager-active");
        window.removeEventListener("keydown", this.handleWindowKeyDown, false);
    }

    preventAndSwitch = (dir: number, e: KeyboardEvent, activeIndex: number) => {
        e && e.preventDefault();

        const newIndex = activeIndex + dir;
        if (0 <= newIndex && newIndex < this.props.pages.length) {
            this.props.onSwitch(newIndex);
        }
    };

    handleContentClick = (e: React.MouseEvent) => {
        // Close only if the user clicked on the content area outside any
        // individual page
        if (e.target === e.currentTarget) {
            this.props.onClose();
        }
    };

    handleWindowKeyDown = (e: KeyboardEvent) => {
        const {activeIndex} = this.props;
        if (activeIndex == null) {
            return;
        }
        if (e.keyCode === 27 /* Esc */) {
            this.props.onClose();
        } else if (e.keyCode === 37 /* ArrowLeft */) {
            this.preventAndSwitch(-1, e, activeIndex);
        } else if (e.keyCode === 39 /* ArrowRight */) {
            this.preventAndSwitch(1, e, activeIndex);
        }
    };

    render(): React.ReactNode {
        const {activeIndex} = this.props;
        if (activeIndex == null) {
            return null;
        }

        const activePage = this.props.pages[activeIndex];
        let leftPage = null;
        if (activeIndex - 1 >= 0) {
            // @ts-expect-error - TS2322 - Type 'ReactElement<any, string | JSXElementConstructor<any>>' is not assignable to type 'null'.
            leftPage = this.props.pages[activeIndex - 1];
        }
        let rightPage = null;
        if (activeIndex + 1 < this.props.pages.length) {
            // @ts-expect-error - TS2322 - Type 'ReactElement<any, string | JSXElementConstructor<any>>' is not assignable to type 'null'.
            rightPage = this.props.pages[activeIndex + 1];
        }

        const overlay = <div key="overlay" className="stories-modal-overlay" />;
        const contents = (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error
            <div
                key="contents"
                className={css(styles.modal)}
                onClick={this.handleContentClick}
            >
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                    href="#"
                    className="modal-pager__arrow-left"
                    // @ts-expect-error - TS2345 - Argument of type 'MouseEvent<HTMLAnchorElement, MouseEvent>' is not assignable to parameter of type 'KeyboardEvent'.
                    onClick={(e) => this.preventAndSwitch(-1, e, activeIndex)}
                >
                    <i className="icon-chevron-left" />
                </a>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                    href="#"
                    className="modal-pager__arrow-right"
                    // @ts-expect-error - TS2345 - Argument of type 'MouseEvent<HTMLAnchorElement, MouseEvent>' is not assignable to parameter of type 'KeyboardEvent'.
                    onClick={(e) => this.preventAndSwitch(1, e, activeIndex)}
                >
                    <i className="icon-chevron-right" />
                </a>
                {leftPage && (
                    <div
                        // @ts-expect-error - TS2339 - Property 'key' does not exist on type 'never'.
                        key={leftPage.key}
                        className="modal-pager__page modal-pager__page-left"
                        onClickCapture={(e) =>
                            // @ts-expect-error - TS2345 - Argument of type 'MouseEvent<HTMLDivElement, MouseEvent>' is not assignable to parameter of type 'KeyboardEvent'.
                            this.preventAndSwitch(-1, e, activeIndex)
                        }
                    >
                        {leftPage}
                    </div>
                )}
                <div
                    key={activePage.key}
                    className="modal-pager__page modal-pager__page-active"
                >
                    {activePage}
                </div>
                {rightPage && (
                    <div
                        // @ts-expect-error - TS2339 - Property 'key' does not exist on type 'never'.
                        key={rightPage.key}
                        className="modal-pager__page modal-pager__page-right"
                        onClickCapture={(e) =>
                            // @ts-expect-error - TS2345 - Argument of type 'MouseEvent<HTMLDivElement, MouseEvent>' is not assignable to parameter of type 'KeyboardEvent'.
                            this.preventAndSwitch(1, e, activeIndex)
                        }
                    >
                        {rightPage}
                    </div>
                )}
            </div>
        );
        return (
            <div className={css(styles.modal)}>
                {overlay}
                {contents}
            </div>
        );
    }
}

type State = {
    activeStoryIndex: number | null | undefined;
    formOpen: boolean;
};

type StoriesPageProps = {
    stories: Array<Story>;
    focusKey: string | null | undefined;
};

class StoriesPage extends React.Component<StoriesPageProps, State> {
    constructor(props: StoriesPageProps) {
        super(props);
        const {focusKey, stories} = props;

        let index = null;
        if (focusKey) {
            // @ts-expect-error - TS2322 - Type 'number' is not assignable to type 'null'.
            index = stories.findIndex((story) => {
                return story.key === focusKey;
            });
        }

        this.state = {
            activeStoryIndex: index && index > -1 ? index : null,
            formOpen: false,
        };
    }

    handleTeaserClick: (i: number) => void = (i: number) => {
        this.setState({activeStoryIndex: i});
    };

    handleStoryClose: () => void = () => {
        this.setState({activeStoryIndex: null});
    };

    handleStorySwitch: (i: number) => void = (i: number) => {
        this.setState({activeStoryIndex: i});
    };

    handleShareClick: () => void = () => {
        this.setState({formOpen: true});
    };

    handleFormClose: () => void = () => {
        this.setState({formOpen: false});
    };

    render(): React.ReactNode {
        const teasers = this.props.stories.map((story, i) => (
            <StoryTeaser
                key={story.key}
                story={story}
                // eslint-disable-next-line react/jsx-no-bind
                onClick={this.handleTeaserClick.bind(null, i)}
            />
        ));
        const pages = this.props.stories.map((story) => (
            <StoryFocus key={story.key} story={story} />
        ));

        return (
            <div className="stories-page">
                <div className="stories-page__banner">
                    <div className="stories-page__banner__contained">
                        <div className="stories-page__banner__share-callout">
                            <Button
                                onClick={this.handleShareClick}
                                style={styles.btn}
                            >
                                {t`Share your story`}
                            </Button>
                        </div>
                        <h1 className="stories-page__banner__tagline">
                            {t`Over 80 million lives have been impacted by Khan Academy, and they continue to inspire us every day.`}
                        </h1>
                    </div>
                </div>
                <StoryForm
                    open={this.state.formOpen}
                    onClose={this.handleFormClose}
                />
                <ModalPager
                    activeIndex={this.state.activeStoryIndex}
                    onClose={this.handleStoryClose}
                    onSwitch={this.handleStorySwitch}
                    pages={pages}
                />
                <div className={css(styles.grid)}>{teasers}</div>
                <div className="stories-page__end-callout">
                    <Button onClick={this.handleShareClick} style={styles.btn}>
                        {t`Share your story`}
                    </Button>
                </div>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    btn: {
        minWidth: 260,
        margin: "5px 0",
    },
    modal: {
        // This should have zindexModal
        zIndex: 1080,
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    grid: {
        maxWidth: 940,
        margin: "auto",
        padding: "60px 0px 30px",
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
    },
});

export default StoriesPage;
